"use client";

import { useState } from "react";

const sections = [
  {
    id: "what-we-collect",
    title: "What we collect",
    content: [
      "Your phone number — so we can identify your account on WhatsApp.",
      "Your display name — as provided by WhatsApp.",
      "The commands and messages you send to the assistant — so we can respond to your requests.",
      "A log of your transactions — amount, recipient, and timestamp. We need this to complete your requests and for record-keeping.",
    ],
  },
  {
    id: "what-we-dont-collect",
    title: "What we don't collect",
    content: [
      "Your WhatsApp conversation history beyond what's needed to fulfill a request.",
      "Passwords, PINs, or raw bank credentials.",
      "Full card or bank account numbers.",
      "Any data from people you haven't introduced us to.",
    ],
  },
  {
    id: "how-the-ai-works",
    title: "How the AI works",
    content: [
      "You're talking to an AI, not a human. The assistant is built to help you send and receive money — nothing else.",
      "Your messages are processed by our AI system to understand what you're asking and carry out the action.",
      "We do not use your personal conversations or transaction data to train AI models.",
      "Some requests may be processed by third-party AI infrastructure. When that happens, the data is anonymized and limited to what's needed.",
    ],
  },
  {
    id: "third-parties",
    title: "Third parties we work with",
    content: [
      "Payment processors — they handle the actual movement of money. We'll list them by name in the app.",
      "AI infrastructure providers — they help process your commands. We share only what's necessary.",
      "WhatsApp (Meta) — your messages travel through their platform. Their own privacy policy applies to what happens on their end.",
    ],
  },
  {
    id: "security",
    title: "How we keep your data safe",
    content: [
      "All data in transit is encrypted using TLS/HTTPS.",
      "We never store your PIN or raw payment credentials.",
      "Access to user data is limited to the people who need it to run the service.",
      "We run regular security reviews.",
    ],
  },
  {
    id: "retention",
    title: "How long we keep your data",
    content: [
      "Transaction logs are kept for up to 7 years. This is required by financial regulations.",
      "Account data is kept as long as your account is active.",
      "If you delete your account, we'll remove your personal data within 30 days, except what we're legally required to keep.",
    ],
  },
  {
    id: "your-rights",
    title: "Your rights",
    content: [
      "You can ask to see what data we have on you.",
      "You can ask us to correct anything that's wrong.",
      "You can request that we delete your data. Send an email to hello@pasoma.ai",
      "You can opt out of communications from us at any time.",
    ],
  },
];

const glanceItems = [
  {
    label: "What we collect",
    value: "Phone number, name, commands, transactions",
  },
  { label: "Why", value: "To run the service and stay compliant" },
  { label: "Who sees it", value: "Our team + payment partners" },
  { label: "AI training", value: "We don't train on your data" },
  { label: "Data retention", value: "Up to 2 years for transactions" },
  { label: "Delete my data", value: "Email hello@pasoma.ai" },
];

export default function PrivacyPolicyPage() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <header style={styles.header}>
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.subtitle}>
            This page explains what data Pasoma collects, how we use it, and
            your rights. Last updated: February 2026.
          </p>
        </header>

        {/* At a Glance */}
        <section style={styles.glanceSection} aria-label="Privacy at a glance">
          <h2 style={styles.sectionHeading}>Overview</h2>
          <div style={styles.glanceGrid}>
            {glanceItems.map((item) => (
              <div key={item.label} style={styles.glanceCard}>
                <span style={styles.glanceLabel}>{item.label}</span>
                <span style={styles.glanceValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Full Policy — Accordion */}
        <section aria-label="Full privacy policy">
          <h2 style={styles.sectionHeading}>Full policy</h2>
          <div style={styles.accordionList}>
            {sections.map((section) => {
              const isOpen = openSection === section.id;
              return (
                <div key={section.id} style={styles.accordionItem}>
                  <button
                    id={`accordion-btn-${section.id}`}
                    aria-expanded={isOpen}
                    aria-controls={`accordion-content-${section.id}`}
                    onClick={() => toggle(section.id)}
                    style={styles.accordionButton}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "rgba(255,255,255,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        isOpen ? "rgba(255,255,255,0.04)" : "transparent";
                    }}
                  >
                    <span style={styles.accordionTitle}>{section.title}</span>
                    <span
                      style={{
                        ...styles.accordionIcon,
                        transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                      }}
                    >
                      +
                    </span>
                  </button>
                  <div
                    id={`accordion-content-${section.id}`}
                    role="region"
                    aria-labelledby={`accordion-btn-${section.id}`}
                    style={{
                      ...styles.accordionContent,
                      maxHeight: isOpen ? "600px" : "0px",
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <ul style={styles.contentList}>
                      {section.content.map((point, i) => (
                        <li key={i} style={styles.contentItem}>
                          <span style={styles.bullet}>—</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Contact */}
        <footer style={styles.footer}>
          <h2 style={styles.sectionHeading}>Questions?</h2>
          <p style={styles.footerText}>
            Reach out at{" "}
            <a href="mailto:hello@pasoma.ai" style={styles.link}>
              hello@pasoma.ai
            </a>
            . We&apos;ll respond within 2 business days.
          </p>
          <div style={styles.footerLinks}>
            <a href="/terms" style={styles.subLink}>
              Terms of Service
            </a>
          </div>
          <div style={styles.footerSeparator} />
          <p style={styles.copyright}>
            © {new Date().getFullYear()} Pasoma. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#0a0a0a",
    color: "#e5e5e5",
    fontFamily: "var(--font-dm), DM Sans, sans-serif",
    padding: "0 1rem",
  },
  container: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "4rem 0 6rem",
  },
  header: {
    marginBottom: "3.5rem",
  },
  backLink: {
    color: "#888",
    fontSize: "0.875rem",
    textDecoration: "none",
    display: "inline-block",
    marginBottom: "2rem",
    transition: "color 0.2s",
  },
  badge: {
    display: "inline-block",
    fontSize: "0.75rem",
    fontWeight: 500,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "#a0a0a0",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "999px",
    padding: "0.3rem 0.75rem",
    marginBottom: "1.25rem",
  },
  title: {
    fontSize: "clamp(2rem, 5vw, 3rem)",
    fontWeight: 700,
    lineHeight: 1.15,
    color: "#ffffff",
    marginBottom: "1rem",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "1rem",
    color: "#888",
    lineHeight: 1.7,
    maxWidth: "560px",
  },
  glanceSection: {
    marginBottom: "3rem",
  },
  sectionHeading: {
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    color: "#666",
    marginBottom: "1.25rem",
  },
  glanceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "1px",
    background: "rgba(255,255,255,0.06)",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.06)",
  },
  glanceCard: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.4rem",
    padding: "1.25rem",
    background: "#0f0f0f",
  },
  glanceLabel: {
    fontSize: "0.75rem",
    color: "#666",
    fontWeight: 500,
    letterSpacing: "0.04em",
  },
  glanceValue: {
    fontSize: "0.9rem",
    color: "#d4d4d4",
    fontWeight: 500,
    lineHeight: 1.4,
  },
  divider: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    margin: "3rem 0",
  },
  accordionList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.06)",
  },
  accordionItem: {
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  accordionButton: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1.25rem 1.5rem",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#e5e5e5",
    textAlign: "left" as const,
    transition: "background 0.2s",
  },
  accordionTitle: {
    fontSize: "0.975rem",
    fontWeight: 500,
    color: "#e5e5e5",
  },
  accordionIcon: {
    fontSize: "1.35rem",
    color: "#666",
    fontWeight: 300,
    lineHeight: 1,
    transition: "transform 0.25s ease",
    flexShrink: 0,
  },
  accordionContent: {
    overflow: "hidden",
    transition: "max-height 0.3s ease, opacity 0.25s ease",
    padding: "0 1.5rem",
  },
  contentList: {
    listStyle: "none",
    margin: 0,
    padding: "0 0 1.5rem",
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.85rem",
  },
  contentItem: {
    display: "flex",
    gap: "0.75rem",
    fontSize: "0.9rem",
    color: "#aaa",
    lineHeight: 1.65,
  },
  bullet: {
    color: "#555",
    flexShrink: 0,
    marginTop: "0.05rem",
  },
  noticeBox: {
    marginTop: "3rem",
    padding: "1.25rem 1.5rem",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "12px",
  },
  noticeText: {
    fontSize: "0.875rem",
    color: "#888",
    lineHeight: 1.7,
    margin: 0,
  },
  footer: {
    marginTop: "4rem",
  },
  footerText: {
    fontSize: "0.95rem",
    color: "#888",
    lineHeight: 1.7,
  },
  link: {
    color: "#e5e5e5",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  footerLinks: {
    marginTop: "1.25rem",
    display: "flex",
    gap: "1.5rem",
  },
  subLink: {
    fontSize: "0.875rem",
    color: "#666",
    textDecoration: "none",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    paddingBottom: "1px",
    transition: "color 0.2s",
  },
  footerSeparator: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    margin: "2.5rem 0 1.5rem",
  },
  copyright: {
    fontSize: "0.8rem",
    color: "#555",
  },
};
